import { Command } from 'commander';
import chalk from 'chalk';
import { getSDKClient } from '../../lib/sdk-client.js';
import { OutputFormatter, getJsonFlag } from '../../lib/output.js';
import { MutagentError, handleError } from '../../lib/errors.js';
import { providerSettingsLink, providerLinks, providerLink } from '../../lib/ui-links.js';
import type { Provider, ProviderType } from '../../types/index.js';
import { registerAddCommand } from './add.js';
import { registerUpdateCommand } from './update.js';
import { registerDeleteCommand } from './delete.js';

// Valid provider types for validation
export const VALID_PROVIDER_TYPES: ProviderType[] = [
  'openai',
  'anthropic',
  'google',
  'azure',
  'bedrock',
  'cohere',
  'mistral',
  'groq',
  'together',
  'replicate',
  'custom',
];

// Command option interfaces for type safety
interface ListOptions {
  limit: string;
  offset?: string;
  type?: string;
  models?: boolean;
}

/**
 * Validates that the provider type is valid
 */
export function validateProviderType(type: string): ProviderType {
  if (!VALID_PROVIDER_TYPES.includes(type as ProviderType)) {
    throw new MutagentError(
      'INVALID_PROVIDER_TYPE',
      `Invalid provider type: ${type}`,
      `Valid types: ${VALID_PROVIDER_TYPES.join(', ')}`
    );
  }
  return type as ProviderType;
}

export function createProvidersCommand(): Command {
  const providers = new Command('providers')
    .description('Manage LLM provider configurations (BYOK)')
    .addHelpText('after', `
Examples:
  ${chalk.dim('$')} mutagent providers list
  ${chalk.dim('$')} mutagent providers get <provider-id>
  ${chalk.dim('$')} mutagent providers add --provider openai --name "My OpenAI" --api-key $KEY
  ${chalk.dim('$')} mutagent providers update <id> --name "New Name"
  ${chalk.dim('$')} mutagent providers delete <id> --force
  ${chalk.dim('$')} mutagent providers test <provider-id>

Provider Types:
  openai, anthropic, google, azure, bedrock, cohere, mistral, groq, together, replicate, custom

Subcommands:
  list, get, add, update, delete, test

Tip:
  Use --models on list to see available models per provider.
    `);

  // =========================================================================
  // LIST PROVIDERS
  // =========================================================================
  providers
    .command('list')
    .description('List all providers')
    .option('-l, --limit <n>', 'Limit results', '50')
    .option('-o, --offset <n>', 'Offset for pagination')
    .option('-t, --type <type>', 'Filter by provider type')
    .option('-m, --models', 'Show available models per provider (calls /providers/catalog)')
    .addHelpText('after', `
Examples:
  ${chalk.dim('$')} mutagent providers list
  ${chalk.dim('$')} mutagent providers list --type openai
  ${chalk.dim('$')} mutagent providers list --models
  ${chalk.dim('$')} mutagent providers list --json
    `)
    .action(async (options: ListOptions) => {
      const isJson = getJsonFlag(providers);
      const output = new OutputFormatter(isJson ? 'json' : 'table');

      try {
        const client = await getSDKClient();
        const filters: { limit?: number; offset?: number; type?: string } = {
          limit: parseInt(options.limit, 10) || 50,
        };

        if (options.offset) {
          filters.offset = parseInt(options.offset, 10);
        }
        if (options.type) {
          filters.type = validateProviderType(options.type);
        }

        const result = await client.listProviders(filters);

        // Fetch catalog if --models requested
        let catalogByKind: Record<string, string[]> = {};
        if (options.models) {
          try {
            const catalog = await client.getModelsCatalog();
            for (const entry of catalog.models) {
              const kind = entry.provider.kind;
              if (!catalogByKind[kind]) catalogByKind[kind] = [];
              catalogByKind[kind].push(entry.id);
            }
          } catch {
            // Best-effort — don't block list on catalog failure
          }
        }

        const formatModels = (kind: string): string => {
          const models = catalogByKind[kind] ?? [];
          if (models.length === 0) return '—';
          if (models.length <= 3) return models.join(', ');
          return `${models.slice(0, 3).join(', ')} +${models.length - 3} more`;
        };

        if (isJson) {
          const withLinks = result.data.map((p: Provider) => ({
            id: p.id,
            name: p.name,
            type: p.type,
            isActive: p.isActive,
            updatedAt: p.updatedAt,
            ...(options.models ? { models: catalogByKind[p.type as string] ?? [] } : {}),
            _links: providerLinks(p.id),
          }));
          output.output({ ...result, data: withLinks });
        } else {
          if (result.data.length === 0) {
            output.info('No providers configured.');
            output.info(`Configure at: ${providerSettingsLink()}`);
          } else {
            const formatted = result.data.map((p: Provider) => ({
              id: p.id,
              name: p.name,
              type: p.type,
              baseUrl: p.baseUrl ?? 'default',
              active: p.isActive ? 'Yes' : 'No',
              updated: p.updatedAt ? new Date(p.updatedAt).toLocaleDateString() : 'N/A',
              ...(options.models ? { models: formatModels(p.type as string) } : {}),
              url: providerLink(p.id),
            }));
            output.output(formatted);
          }
        }
      } catch (error) {
        handleError(error, isJson);
      }
    });

  // =========================================================================
  // GET PROVIDER
  // =========================================================================
  providers
    .command('get')
    .description('Get provider details')
    .argument('<id>', 'Provider ID (from: mutagent providers list)')
    .addHelpText('after', `
Examples:
  ${chalk.dim('$')} mutagent providers get <provider-id>
  ${chalk.dim('$')} mutagent providers get <provider-id> --json
    `)
    .action(async (id: string) => {
      const isJson = getJsonFlag(providers);
      const output = new OutputFormatter(isJson ? 'json' : 'table');

      try {
        const client = await getSDKClient();
        const provider = await client.getProvider(id);

        if (isJson) {
          output.output({ ...provider, _links: providerLinks(provider.id) });
        } else {
          const formatted: Record<string, unknown> = {
            id: provider.id,
            name: provider.name,
            type: provider.type,
            baseUrl: provider.baseUrl ?? 'default',
            isActive: provider.isActive ? 'Yes' : 'No',
            createdBy: provider.createdBy ?? 'N/A',
            createdAt: provider.createdAt ? new Date(provider.createdAt).toLocaleString() : 'N/A',
            updatedAt: provider.updatedAt ? new Date(provider.updatedAt).toLocaleString() : 'N/A',
            url: providerLink(provider.id),
          };
          output.output(formatted);
        }
      } catch (error) {
        handleError(error, isJson);
      }
    });

  // =========================================================================
  // TEST PROVIDER
  // =========================================================================
  providers
    .command('test')
    .description('Test provider connectivity')
    .argument('<id>', 'Provider ID (from: mutagent providers list)')
    .addHelpText('after', `
Examples:
  ${chalk.dim('$')} mutagent providers test <provider-id>
  ${chalk.dim('$')} mutagent providers test <provider-id> --json

${chalk.dim('Tests connectivity and lists available models for the provider.')}
    `)
    .action(async (id: string) => {
      const isJson = getJsonFlag(providers);
      const output = new OutputFormatter(isJson ? 'json' : 'table');

      try {
        const client = await getSDKClient();

        if (!isJson) {
          output.info(`Testing provider ${id}...`);
        }

        const result = await client.testProvider(id);

        if (isJson) {
          output.output({ ...result, _links: providerLinks(id) });
        } else {
          if (result.success) {
            output.success(`Provider test passed (${String(result.responseTimeMs)}ms)`);
            console.log(chalk.green(`Message: ${result.message}`));
            if (result.availableModels && result.availableModels.length > 0) {
              console.log(chalk.bold('\nAvailable Models:'));
              result.availableModels.forEach(model => {
                console.log(`  - ${model}`);
              });
            }
          } else {
            output.error(`Provider test failed: ${result.message}`);
            if (result.error) {
              console.log(chalk.red(`Error: ${result.error}`));
            }
          }
        }
      } catch (error) {
        handleError(error, isJson);
      }
    });

  // Register write subcommands from separate files
  registerAddCommand(providers);
  registerUpdateCommand(providers);
  registerDeleteCommand(providers);

  return providers;
}
